import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import { Calendar, Plus, Edit2, Trash2, Ticket, Loader2, AlertCircle, Upload, X, Image as ImageIcon } from 'lucide-react';

const EventsExhibitions = ({ authToken }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [ticketsModal, setTicketsModal] = useState(null);
  const [purchasesModal, setPurchasesModal] = useState(null);
  const [form, setForm] = useState({
    title: '', description: '', short_description: '', venue: '', address: '',
    start_at: '', end_at: '', image_path: '', status: 'draft', max_attendees: '',
    art_styles: '', featured_artists: '', highlights: '', ticket_price: '', total_seats: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchEvents = () => {
    setLoading(true);
    setError('');
    fetch(`${API_BASE_URL}/admin/super/events`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((res) => {
        if (res.status === 404) return Promise.reject(new Error('Backend route not found. Run Flask locally (python app.py) or deploy super admin routes.'));
        return res.ok ? res.json() : res.json().then((e) => Promise.reject(e));
      })
      .then((data) => setEvents(data.events || []))
      .catch((err) => setError(err.message || err.error || 'Failed to load events'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchEvents(); }, [authToken]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('Image must be less than 2MB');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaving(true);
    const url = modal?.event_id ? `${API_BASE_URL}/admin/super/events/${modal.event_id}` : `${API_BASE_URL}/admin/super/events`;
    const method = modal?.event_id ? 'PUT' : 'POST';

    // Use FormData for image upload
    const formData = new FormData();
    formData.append('title', form.title);
    formData.append('description', form.description);
    formData.append('short_description', form.short_description);
    formData.append('venue', form.venue);
    formData.append('address', form.address);
    formData.append('start_at', form.start_at || '');
    formData.append('end_at', form.end_at || '');
    formData.append('status', form.status);
    formData.append('ticket_price', form.ticket_price ? parseFloat(form.ticket_price) : 0);
    formData.append('total_seats', form.total_seats ? parseInt(form.total_seats, 10) : 0);
    formData.append('max_attendees', form.total_seats ? parseInt(form.total_seats, 10) : (form.max_attendees ? parseInt(form.max_attendees, 10) : 0));

    // Array fields
    formData.append('art_styles', JSON.stringify(form.art_styles ? form.art_styles.split(',').map(s => s.trim()) : []));
    formData.append('featured_artists', JSON.stringify(form.featured_artists ? form.featured_artists.split(',').map(s => s.trim()) : []));
    formData.append('highlights', JSON.stringify(form.highlights ? form.highlights.split(',').map(s => s.trim()) : []));

    if (imageFile) {
      formData.append('main_image', imageFile);
    } else if (form.image_path) {
      formData.append('image_path', form.image_path);
    }

    fetch(url, {
      method,
      headers: { Authorization: `Bearer ${authToken}` },
      body: formData,
    })
      .then((res) => (res.ok ? res.json() : res.json().then((e) => Promise.reject(e))))
      .then(() => {
        setModal(null);
        setForm({
          title: '', description: '', short_description: '', venue: '', address: '',
          start_at: '', end_at: '', image_path: '', status: 'draft', max_attendees: '',
          art_styles: '', featured_artists: '', highlights: '', ticket_price: '', total_seats: ''
        });
        setImageFile(null);
        setImagePreview(null);
        fetchEvents();
      })
      .catch((err) => setError(err.message || err.error || 'Save failed'))
      .finally(() => setSaving(false));
  };

  const openEdit = (ev) => {
    setModal(ev);
    setImageFile(null);
    setImagePreview(null);
    setForm({
      title: ev.title || '', description: ev.description || '', short_description: ev.short_description || '',
      venue: ev.venue || '', address: ev.address || '', start_at: ev.start_at ? ev.start_at.slice(0, 16) : '', end_at: ev.end_at ? ev.end_at.slice(0, 16) : '',
      image_path: ev.image_path || '', status: ev.status || 'draft', max_attendees: ev.max_attendees ?? '',
      art_styles: Array.isArray(ev.art_styles) ? ev.art_styles.join(', ') : (ev.art_styles || ''),
      featured_artists: Array.isArray(ev.featured_artists) ? ev.featured_artists.join(', ') : (ev.featured_artists || ''),
      highlights: Array.isArray(ev.highlights) ? ev.highlights.join(', ') : (ev.highlights || ''),
      ticket_price: ev.ticket_price || '',
      total_seats: ev.total_seats || '',
    });
  };

  const deleteEvent = (event_id) => {
    if (!window.confirm('Delete this event?')) return;
    fetch(`${API_BASE_URL}/admin/super/events/${event_id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${authToken}` } })
      .then((res) => (res.ok ? res.json() : res.json().then((e) => Promise.reject(e))))
      .then(() => { setModal(null); fetchEvents(); })
      .catch((err) => setError(err.message || err.error || 'Delete failed'));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-4">Events & Exhibitions</h2>
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center text-red-700">
          <AlertCircle className="h-4 w-4 mr-2" />
          {error}
        </div>
      )}
      <div className="mb-4 flex justify-end">
        <button
          type="button"
          onClick={() => {
            setModal({});
            setImageFile(null);
            setImagePreview(null);
            setForm({
              title: '', description: '', short_description: '', venue: '', address: '',
              start_at: '', end_at: '', image_path: '', status: 'draft', max_attendees: '',
              art_styles: '', featured_artists: '', highlights: '', ticket_price: '', total_seats: ''
            });
          }}
          className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        >
          <Plus className="h-4 w-4 mr-2" /> Add Event
        </button>
      </div>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Venue</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {events.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">No events yet.</td>
              </tr>
            ) : (
              events.map((ev) => (
                <tr key={ev.event_id}>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <div className="flex items-center">
                      {ev.image_path && (
                        <img
                          src={ev.image_path.startsWith('http') ? ev.image_path : `${API_BASE_URL}${ev.image_path}`}
                          alt=""
                          className="h-8 w-8 rounded object-cover mr-3 border shadow-sm"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )}
                      <span>{ev.title}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{ev.venue || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{ev.start_at ? new Date(ev.start_at).toLocaleString() : '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${ev.status === 'active' ? 'bg-green-100 text-green-800' : ev.status === 'draft' ? 'bg-gray-100 text-gray-800' : 'bg-red-100 text-red-800'}`}>
                      {ev.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" onClick={() => setTicketsModal(ev)} className="text-blue-600 hover:underline mr-2" title="Tickets">Tickets</button>
                    <button type="button" onClick={() => setPurchasesModal(ev)} className="text-blue-600 hover:underline mr-2" title="Purchases">Purchases</button>
                    <button type="button" onClick={() => openEdit(ev)} className="text-indigo-600 hover:underline mr-2">Edit</button>
                    <button type="button" onClick={() => deleteEvent(ev.event_id)} className="text-red-600 hover:underline">Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">{modal.event_id ? 'Edit Event' : 'Add Event'}</h3>
              <form onSubmit={handleSubmit}>
                <div className="space-y-3">
                  <input type="text" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border rounded px-3 py-2" required />
                  <input type="text" placeholder="Venue" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} className="w-full border rounded px-3 py-2" />
                  <input type="text" placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full border rounded px-3 py-2" />
                  <input type="datetime-local" placeholder="Start" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} className="w-full border rounded px-3 py-2" />
                  <input type="datetime-local" placeholder="End" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} className="w-full border rounded px-3 py-2" />

                  {/* Image Upload Section */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Event Image</label>
                    <div className="flex items-center gap-4">
                      {imagePreview || form.image_path ? (
                        <div className="relative w-32 h-32 rounded-lg overflow-hidden border">
                          <img
                            src={imagePreview || (form.image_path?.startsWith('http') ? form.image_path : `${API_BASE_URL}${form.image_path}`)}
                            alt="Preview"
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setImageFile(null);
                              setImagePreview(null);
                              setForm({ ...form, image_path: '' });
                            }}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 shadow-md"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <Upload className="h-6 w-6 text-gray-400 mb-2" />
                            <p className="text-xs text-gray-500 font-medium tracking-tight">Upload</p>
                          </div>
                          <input type="file" className="hidden" accept="image/*" onChange={handleImageChange} />
                        </label>
                      )}
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">
                          Recommended size: 1200x600 pixels.<br />
                          Max size: 2MB.
                        </p>
                        {!imagePreview && !form.image_path && (
                          <input
                            type="text"
                            placeholder="Or enter Image URL"
                            value={form.image_path}
                            onChange={(e) => setForm({ ...form, image_path: e.target.value })}
                            className="mt-2 w-full text-xs border rounded px-2 py-1"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border rounded px-3 py-2">
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="ended">Ended</option>
                  </select>
                  <div className="grid grid-cols-2 gap-3">
                    <input type="number" placeholder="Ticket Price (₹)" value={form.ticket_price} onChange={(e) => setForm({ ...form, ticket_price: e.target.value })} className="border rounded px-3 py-2" />
                    <input type="number" placeholder="Total Seats" value={form.total_seats} onChange={(e) => setForm({ ...form, total_seats: e.target.value })} className="border rounded px-3 py-2" />
                  </div>
                  <input type="text" placeholder="Art Styles (comma separated)" value={form.art_styles} onChange={(e) => setForm({ ...form, art_styles: e.target.value })} className="w-full border rounded px-3 py-2" />
                  <input type="text" placeholder="Featured Artists (comma separated)" value={form.featured_artists} onChange={(e) => setForm({ ...form, featured_artists: e.target.value })} className="w-full border rounded px-3 py-2" />
                  <textarea placeholder="Highlights (comma separated)" value={form.highlights} onChange={(e) => setForm({ ...form, highlights: e.target.value })} className="w-full border rounded px-3 py-2" rows={2} />
                  <textarea placeholder="Short description" value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} className="w-full border rounded px-3 py-2" rows={2} />
                  <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border rounded px-3 py-2" rows={3} />
                </div>
                <div className="mt-4 flex justify-end gap-2">
                  <button type="button" onClick={() => setModal(null)} className="px-4 py-2 border rounded">Cancel</button>
                  <button type="submit" disabled={saving} className="px-4 py-2 bg-indigo-600 text-white rounded disabled:opacity-50">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin inline" /> : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {ticketsModal && (
        <TicketsModal event={ticketsModal} authToken={authToken} onClose={() => setTicketsModal(null)} onSaved={() => setTicketsModal(null)} />
      )}
      {purchasesModal && (
        <PurchasesModal event={purchasesModal} authToken={authToken} onClose={() => setPurchasesModal(null)} />
      )}
    </div>
  );
};

function TicketsModal({ event, authToken, onClose, onSaved }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addType, setAddType] = useState('');
  const [addPrice, setAddPrice] = useState('');
  const [addQty, setAddQty] = useState('');
  const [addOrder, setAddOrder] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!event?.event_id) return;
    fetch(`${API_BASE_URL}/admin/super/events/${event.event_id}/tickets`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((res) => (res.ok ? res.json() : res.json().then((e) => Promise.reject(e))))
      .then((data) => setTickets(data.tickets || []))
      .finally(() => setLoading(false));
  }, [event?.event_id, authToken]);

  const addTicket = (e) => {
    e.preventDefault();
    if (!addType) return;
    setSaving(true);
    fetch(`${API_BASE_URL}/admin/super/events/${event.event_id}/tickets`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticket_type: addType, price: addPrice || 0, quantity_total: addQty || 0, sort_order: addOrder }),
    })
      .then((res) => (res.ok ? res.json() : res.json().then((e) => Promise.reject(e))))
      .then(() => {
        setAddType(''); setAddPrice(''); setAddQty(''); setAddOrder(0);
        fetch(`${API_BASE_URL}/admin/super/events/${event.event_id}/tickets`, { headers: { Authorization: `Bearer ${authToken}` } })
          .then((r) => r.json())
          .then((d) => setTickets(d.tickets || []));
      })
      .finally(() => setSaving(false));
  };

  const deleteTicket = (ticket_id) => {
    if (!window.confirm('Delete this ticket type?')) return;
    fetch(`${API_BASE_URL}/admin/super/events/${event.event_id}/tickets/${ticket_id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${authToken}` } })
      .then((res) => (res.ok ? res.json() : res.json().then((e) => Promise.reject(e))))
      .then(() => setTickets((prev) => prev.filter((t) => t.id !== ticket_id)));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-2">Tickets: {event.title}</h3>
          {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : (
            <>
              <ul className="mb-4 space-y-2">
                {tickets.map((t) => (
                  <li key={t.id} className="flex justify-between items-center border-b pb-2">
                    <span>{t.ticket_type} — ₹{t.price} ({(t.quantity_total || 0) - (t.quantity_sold || 0)} left)</span>
                    <button type="button" onClick={() => deleteTicket(t.id)} className="text-red-600 text-sm">Delete</button>
                  </li>
                ))}
              </ul>
              <form onSubmit={addTicket} className="flex flex-wrap gap-2 items-end">
                <input type="text" placeholder="Type (e.g. General)" value={addType} onChange={(e) => setAddType(e.target.value)} className="border rounded px-2 py-1 flex-1 min-w-0" />
                <input type="number" placeholder="Price" value={addPrice} onChange={(e) => setAddPrice(e.target.value)} className="border rounded px-2 py-1 w-20" />
                <input type="number" placeholder="Qty" value={addQty} onChange={(e) => setAddQty(e.target.value)} className="border rounded px-2 py-1 w-20" />
                <input type="number" placeholder="Order" value={addOrder} onChange={(e) => setAddOrder(parseInt(e.target.value, 10) || 0)} className="border rounded px-2 py-1 w-16" />
                <button type="submit" disabled={saving} className="px-3 py-1 bg-indigo-600 text-white rounded text-sm">Add</button>
              </form>
            </>
          )}
          <button type="button" onClick={onClose} className="mt-4 w-full py-2 border rounded">Close</button>
        </div>
      </div>
    </div>
  );
}

function PurchasesModal({ event, authToken, onClose }) {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createEmail, setCreateEmail] = useState('');
  const [createName, setCreateName] = useState('');
  const [createTicketId, setCreateTicketId] = useState('');
  const [createQty, setCreateQty] = useState(1);
  const [tickets, setTickets] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!event?.event_id) return;
    Promise.all([
      fetch(`${API_BASE_URL}/admin/super/events/${event.event_id}/purchases`, { headers: { Authorization: `Bearer ${authToken}` } }).then((r) => r.json()),
      fetch(`${API_BASE_URL}/admin/super/events/${event.event_id}/tickets`, { headers: { Authorization: `Bearer ${authToken}` } }).then((r) => r.json()),
    ]).then(([p, t]) => {
      setPurchases(p.purchases || []);
      setTickets(t.tickets || []);
    }).finally(() => setLoading(false));
  }, [event?.event_id, authToken]);

  const createPurchase = (e) => {
    e.preventDefault();
    if (!createEmail || !createTicketId) return;
    setSaving(true);
    fetch(`${API_BASE_URL}/admin/super/events/${event.event_id}/purchases`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ customer_email: createEmail, customer_name: createName, event_ticket_id: parseInt(createTicketId, 10), quantity: createQty || 1 }),
    })
      .then((res) => (res.ok ? res.json() : res.json().then((e) => Promise.reject(e))))
      .then(() => {
        setCreateEmail(''); setCreateName(''); setCreateTicketId(''); setCreateQty(1);
        fetch(`${API_BASE_URL}/admin/super/events/${event.event_id}/purchases`, { headers: { Authorization: `Bearer ${authToken}` } })
          .then((r) => r.json())
          .then((d) => setPurchases(d.purchases || []));
      })
      .finally(() => setSaving(false));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-2">Ticket Purchases: {event.title}</h3>
          {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : (
            <>
              <div className="mb-4 p-3 bg-gray-50 rounded">
                <h4 className="font-medium mb-2">Create purchase (QR generated)</h4>
                <form onSubmit={createPurchase} className="flex flex-wrap gap-2 items-end">
                  <input type="email" placeholder="Customer email" value={createEmail} onChange={(e) => setCreateEmail(e.target.value)} className="border rounded px-2 py-1 flex-1 min-w-0" required />
                  <input type="text" placeholder="Name" value={createName} onChange={(e) => setCreateName(e.target.value)} className="border rounded px-2 py-1 w-32" />
                  <select value={createTicketId} onChange={(e) => setCreateTicketId(e.target.value)} className="border rounded px-2 py-1">
                    <option value="">Ticket type</option>
                    {tickets.map((t) => (
                      <option key={t.id} value={t.id}>{t.ticket_type} ₹{t.price}</option>
                    ))}
                  </select>
                  <input type="number" min={1} value={createQty} onChange={(e) => setCreateQty(e.target.value)} className="border rounded px-2 py-1 w-16" />
                  <button type="submit" disabled={saving} className="px-3 py-1 bg-green-600 text-white rounded text-sm">Create + QR</button>
                </form>
              </div>
              <ul className="space-y-2">
                {purchases.map((p) => (
                  <li key={p.id} className="flex justify-between items-center border-b pb-2 text-sm">
                    <span>{p.customer_email} — {p.quantity} × ₹{p.amount} — {p.purchase_id} {p.qr_code_path ? '(QR)' : ''}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
          <button type="button" onClick={onClose} className="mt-4 w-full py-2 border rounded">Close</button>
        </div>
      </div>
    </div>
  );
}

export default EventsExhibitions;
